import React, { useRef, useEffect, ElementType, ReactNode } from 'react';
import { gsap, SplitText, ScrollTrigger, EASE, prefersReducedMotion } from './gsap';

interface RevealTextProps {
    children: ReactNode;
    /** Rendered element. Defaults to a div so the caller controls semantics. */
    as?: ElementType;
    className?: string;
    /** Extra delay before the first line opens. */
    delay?: number;
    /** Gap between consecutive lines. The reference sits around 0.08–0.1s. */
    stagger?: number;
    /** ScrollTrigger start. */
    start?: string;
    id?: string;
}

/**
 * The signature augustus.com headline reveal: text is split into lines, each
 * line is masked, and the lines slide up out of their mask on a hard
 * power4.out with a short stagger.
 *
 * Falls back to plain visible text when the user prefers reduced motion.
 */
const RevealText: React.FC<RevealTextProps> = ({
    children,
    as: Tag = 'div',
    className = '',
    delay = 0,
    stagger = 0.09,
    start = 'top 88%',
    id,
}) => {
    const ref = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        if (prefersReducedMotion()) {
            el.classList.add('aug-reveal-ready');
            return;
        }

        let ctx: gsap.Context | undefined;
        let split: SplitText | undefined;
        let cancelled = false;

        // Splitting before webfonts settle produces wrong line breaks.
        const fontsReady =
            typeof document !== 'undefined' && 'fonts' in document
                ? document.fonts.ready
                : Promise.resolve();

        fontsReady.then(() => {
            if (cancelled || !ref.current) return;

            ctx = gsap.context(() => {
                split = SplitText.create(el, {
                    type: 'lines',
                    mask: 'lines',
                    linesClass: 'aug-split-line',
                    autoSplit: true,
                    onSplit: (self) =>
                        gsap.from(self.lines, {
                            yPercent: 110,
                            duration: 1,
                            ease: EASE.reveal,
                            stagger,
                            delay,
                            scrollTrigger: {
                                trigger: el,
                                start,
                                once: true,
                            },
                        }),
                });
            }, el);

            el.classList.add('aug-reveal-ready');
            ScrollTrigger.refresh();
        });

        return () => {
            cancelled = true;
            split?.revert();
            ctx?.revert();
        };
    }, [delay, stagger, start]);

    return (
        <Tag id={id} ref={ref} className={className}>
            {children}
        </Tag>
    );
};

export default RevealText;
